import {afterRenderEffect, AfterViewInit, ComponentRef, Directive, ElementRef, inject, input, Renderer2, ViewContainerRef} from '@angular/core';
import {MatPaginator} from '@angular/material/paginator';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {PageNumberControlsComponent} from './page-number-controls/page-number-controls.component';

/**
 * This directive was based on the idea supplied in this Medium article:
 * https://medium.com/multitude-it-labs/angular-custom-matpaginator-styling-fa91e697fd88
 *
 * There is no native way to make the mat-paginator display page numbers, and this seems to be the most accepted method of adapting it.  While this still
 * does some light DOM manipulation, I cut down on that a LOT from the original implementation.  I also updated our custom solution to use a more standard
 * component (PageNumberControlsComponent) to keep track of and render the actual page number controls.  This makes it much easier to understand and maintain.
 */
@Directive({
  selector: '[mxPageNumberPagination]'
})
export class PageNumberPaginationDirective implements AfterViewInit {

  private readonly paginator = inject(MatPaginator, {
    optional: true,
    self: true,
    host: true,
  });

  private readonly element = inject(ElementRef).nativeElement;
  private readonly renderer = inject(Renderer2);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private pageNumberComponentRef?: ComponentRef<PageNumberControlsComponent>;

  // Input signals that update any time the user changes the page size dropdown or the number of items to be paginated changes.
  readonly pageSize = input<number>(0);
  readonly length = input<number>(0);

  constructor() {
    // The paginator's page was updated via the built-in last/next arrow controls or from the route params, so update our component's pageIndex.
    this.paginator?.page
      .pipe(takeUntilDestroyed())
      .subscribe(event => {
        this.pageNumberComponentRef?.setInput('currentPage', event.pageIndex);
      });
  }

  ngAfterViewInit(): void {
    this.createPageNumberComponent();
  }

  /**
   * Creates and inserts the page number component into the DOM and updates the layout of the paginator controls
   */
  private createPageNumberComponent(): void {
    const paginatorRow = this.element.querySelector('.mat-mdc-paginator-container');
    const actionsContainer = this.element.querySelector('.mat-mdc-paginator-range-actions');
    const nextButton = this.element.querySelector('button.mat-mdc-paginator-navigation-next');
    const itemsDisplayedLabel = this.element.querySelector('.mat-mdc-paginator-range-label');
    const itemsPerPage = this.element.querySelector('.mat-mdc-paginator-page-size');

    if (this.paginator && actionsContainer && nextButton) {
      // Add a class to the paginator row so our stylesheet can lay it out correctly
      this.renderer.addClass(paginatorRow, 'page-number-layout');

      this.pageNumberComponentRef = this.viewContainerRef.createComponent(PageNumberControlsComponent);
      this.syncComponentInputs();

      // Any time the page number is changed from our new controls, update the paginator.
      this.pageNumberComponentRef.instance.pageSelected
        .subscribe(pageIndex => {
          this.switchPage(pageIndex);
        });

      const pageNumberContainerElement = this.pageNumberComponentRef.location.nativeElement;

      // Place the page numbers component before the 'Next' button
      this.renderer.insertBefore(actionsContainer, pageNumberContainerElement, nextButton);

      // Move itemsDisplayedLabel to the left of the controls
      this.renderer.insertBefore(paginatorRow, itemsDisplayedLabel, actionsContainer);

      // Move itemsPerPage to the right of the controls
      const endOfControls = this.renderer.nextSibling(actionsContainer);
      this.renderer.insertBefore(paginatorRow, itemsPerPage, endOfControls);
    }
  }

  /**
   * Subscription to the `pageSize` and `length` input signals.  Any time one changes, sync our component's inputs to match the paginator component.
   */
  readonly pageSizeOrLengthUpdatedEffect = afterRenderEffect(() => {
    this.pageSize();
    this.length();
    this.syncComponentInputs();
  });

  /**
   * Keep the page number component inputs in sync with the paginator.
   */
  private syncComponentInputs(): void {
    if (this.paginator && this.pageNumberComponentRef) {
      this.pageNumberComponentRef.setInput('length', this.paginator.length);
      this.pageNumberComponentRef.setInput('pageSize', this.paginator.pageSize);
      this.pageNumberComponentRef.setInput('currentPage', this.paginator.pageIndex);
    }
  }

  /**
   * Emits a page change event to the paginator component.
   */
  private switchPage(pageIndex: number): void {
    if (this.paginator) {
      const previousPageIndex = this.paginator.pageIndex;
      const totalPages = Math.ceil(this.paginator.length / this.paginator.pageSize);

      // If the page actually changed and is a real page number, emit a pagination event.
      if (pageIndex !== previousPageIndex && pageIndex >= 0 && pageIndex < totalPages) {
        this.paginator.pageIndex = pageIndex;
        this.paginator.page.emit({
          pageIndex,
          pageSize: this.paginator.pageSize,
          length: this.paginator.length,
          previousPageIndex
        });
      }
    }
  }
}
